import { formatRM, type DiscountRule } from '@bazaar/shared';

export interface DiscountRuleSummaryInput {
  kind: 'percent' | 'amount';
  /** Whole percent 1..100 - as entered in the editor, not basis points. */
  percent?: number;
  amountCents?: number;
  /** One per condition, already resolved to a display name (product or category). */
  lines: Array<{ minQty: number; label: string }>;
}

/**
 * The admin list/editor's plain-language summary, e.g.
 * "Buy 4 × Keychains → 10% off" or "Buy 2 × Keychains + 1 × Tote bag → RM 10.00 off"
 * (spec §4). Shared by the rule list and the editor's live preview sentence
 * so the two can never drift apart.
 */
export function summarizeDiscountRuleDraft(input: DiscountRuleSummaryInput): string {
  if (input.lines.length === 0) return 'Add at least one condition to see a preview.';
  const conditions = input.lines.map((l) => `${l.minQty} × ${l.label || '…'}`).join(' + ');
  const effect =
    input.kind === 'percent' ? `${input.percent ?? 0}% off` : `${formatRM(input.amountCents ?? 0)} off`;
  return `Buy ${conditions} → ${effect}`;
}

export function summarizeDiscountRule(rule: DiscountRule): string {
  return summarizeDiscountRuleDraft({
    kind: rule.kind,
    percent: rule.percentBp !== null ? rule.percentBp / 100 : undefined,
    amountCents: rule.amountCents ?? undefined,
    lines: rule.lines.map((l) => ({
      minQty: l.minQty,
      label: l.targetType === 'product' ? l.productName ?? 'product' : l.categoryName ?? 'category',
    })),
  });
}
